import React, { useState, useEffect } from "react"

import "./index.scss"
import { confirmAlert } from "react-confirm-alert" // Import
import { connect, useSelector } from "react-redux"
import "react-confirm-alert/src/react-confirm-alert.css" // Import css
import cn from "classnames"

import { SvgIcon } from "@components/common"
import { statusText } from "@config/mappings"
import { teeupStatusKeys } from "@configs/enums"
import I18n from "@i18n"
import Toast from "@ui/toast"
import { getStageOptions } from "@utils/teeupStateUtils"
import { Stage } from "types/teeup"

import { updateTeeupStatus } from "../../../middlewares/backendMiddleware"
import {
  selectActiveTeeup,
  selectActiveTeeupGameplans,
} from "../../../selectors/activeTeeup"
import { selectUserId } from "../../../selectors/user"

import StageModal from "./StageModal"
import { StatusDot } from "./dropdownStyles"

const { planning, allset, happeningSoon, happening, ended, cancelled } =
  teeupStatusKeys

const messages = {
  planning:
    "Changing the TeeUp state back to Planning, will result in the When suggestion being removed from the Game Plan. Do you want to continue?",
  allset: "Are you sure you want to mark the TeeUp as All Set?",
  allsetNoDecided:
    "A suggestion with an exact date and time must be in the Game Plan to change the TeeUp to All Set",
  happening: "Are you sure you want to switch the TeeUp to Happening Now?",
  ended: "Are you sure you want to end the TeeUp?",
}

const DropDown = ({ userId, teeUp, active, isHigh = false }) => {
  const [status, setStatus] = useState<Stage | null>(null)
  const { settings, organisers } = teeUp
  // CREATE-AI-POLISH 2026-07-03: post-create navigation races setTeeupActive()'s async overview
  // enrichment -- its FIRST dispatch stores the locally-built teeup (createTeeupActions.js) which
  // has NO organisers key yet, so this component could mount before the enriched update landed and
  // crash the whole detail page ("something went wrong" error boundary) on `organisers.map`.
  // Click-navigation never hit this (getTeeupById awaits the overview before dispatching). Guard
  // the transient state; the enriched dispatch re-renders with the real organisers moments later.
  const organizer = (organisers || []).map(({ id }) => id).includes(userId)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isOnlyGeneralWhenSuggestion, setIsOnlyGeneralWhenSuggestion] =
    useState(false)
  const gameplan = useSelector(selectActiveTeeupGameplans)
  console.log("isOnlyGeneralWhenSuggestion", isOnlyGeneralWhenSuggestion)

  // FIX 2026-07-04 (stage chip full color treatment, iPhone parity): the hero-header instance
  // (isHigh) needs the same tinted-background + colored-text + colored-border treatment as the
  // inbox StageLabel (getStageOptionsByStatusId) -- previously only color+backgroundColor were
  // set here and the header's own CSS forced a dark translucent pill over top (see
  // teeupOverviewHeaderStyles.js), which is what actually hid the per-stage colors. Border is
  // scoped to isHigh so the other DropDown call sites (TeeupStatus sidebar, OverviewHeader list
  // row) are untouched.
  const statusButtonStyles = {
    color: status?.color,
    backgroundColor: isModalOpen ? status?.color : status?.backgroundColor,
    ...(isHigh ? { border: `1.5px solid ${status?.color}` } : {}),
  }

  useEffect(() => {
    setIsOnlyGeneralWhenSuggestion(
      gameplan?.[0]?.suggestions?.every(item => item?.isCustomDate)
    )
  }, [gameplan])

  useEffect(() => {
    if (!active) return

    setStatus(
      getStageOptions().find(({ label }) => label === active.status) || null
    )
  }, [active])

  const openModal = () => {
    setIsModalOpen(!isModalOpen)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const isAllowPromoteToAllSetOrPlaning = (option: Stage) => {
    return (
      (option.label === allset || option.label === planning) &&
      settings.allowPromoteToAllSetOrPlanning
    )
  }

  const submitConfirmAlertHandle = option => {
    const isSuggestionsDecided =
      gameplan[0].suggestions.some(item => item?.decided || item?.isDecided) &&
      gameplan[1].suggestions.some(item => item?.decided || item?.isDecided)

    let message = messages[option.label]
    let buttons = [
      {
        label: "No",
        onClick: closeModal,
      },
      {
        label: "Yes",
        onClick: () => {
          setStatus(option.label)
          updateTeeupStatus(userId, option)
          closeModal()
        },
      },
    ]

    if (!isSuggestionsDecided && option.label === allset) {
      message = messages.allsetNoDecided
      buttons = [{ label: "Okay", onClick: closeModal }]
    }

    confirmAlert({
      message,
      buttons,
    })
  }

  const changeStatus = (option: Stage) => {
    // FIX 2026-07-04 (terminal stages don't offer revert -- QJ: use the iPhone's canonical
    // pre-flight-block + toast pattern, not a silent return). Ended/Cancelled are terminal per the
    // backend (lifecycle.py:19 -- "cancelled/ended terminal, never overridden"; change_stage 400s
    // at coordination_objects.py:736-737) and per the mobile gold standard: no reopen affordance,
    // blocked pre-flight at the entry point with Toast.show(Translation.isAlready*Warning) (mobile
    // gameplansCardContainer.js:149-156, strings from mobile i18n/en.js:213-216, now mirrored in
    // this web app's src/i18n/en.js). The web previously still opened the Ended->Planning confirm
    // dialog, which the shim then 400'd silently -- looked like "revert confirms then does
    // nothing". This does NOT affect the legitimate All Set -> Planning demotion path (status?.label
    // is only ended/cancelled when the TeeUp is actually in one of those terminal stages).
    if (status?.label === ended) {
      Toast.show(I18n.t("isAlreadyEndedWarning"), { bottomOffset: 8 })
      return
    }
    if (status?.label === cancelled) {
      Toast.show(I18n.t("isAlreadyCancelledWarning"), { bottomOffset: 8 })
      return
    }

    if (
      option.label === happeningSoon ||
      (option.label === happening && isOnlyGeneralWhenSuggestion) ||
      // DISPLAY-only stage (derived from When/Where decision state, see teeupStateUtils.ts BUG-036
      // comment) -- shown in the visible TimeLine sequence but never a manually selectable
      // transition.
      option.label === "scheduled"
    ) {
      return
    }

    if (option.label === planning && status?.label !== allset) {
      return submitConfirmAlertHandle(option)
    }

    if (
      (organizer || isAllowPromoteToAllSetOrPlaning(option)) &&
      settings.allowChangeState
    ) {
      if (option.label === planning) {
        setStatus(option)
        setIsModalOpen(false)
        updateTeeupStatus(userId, option)
      } else {
        submitConfirmAlertHandle(option)
      }
    }
  }

  // const cancelTeeUp = () => {
  //   confirmAlert({
  //     title: "Cancel TeeUp?",
  //     message: `Are you sure you want to cancel "${teeUp.name}"? This will end the TeeUp for all participants and be archived. This action cannot be undone.`,
  //     buttons: [
  //       {
  //         label: "Go Back",
  //         onClick: () => setIsModalOpen(false),
  //       },
  //       {
  //         label: "Cancel",
  //         onClick: () => {
  //           setStatus(cancelled)
  //           setIsModalOpen(false)
  //           updateTeeupArchiveStatus(teeUp.id)
  //           updateTeeupStatus(
  //             userId,
  //             teeupStatusOptions[teeupStatusOptions.length - 1]
  //           )
  //         },
  //       },
  //     ],
  //   })
  // }

  return (
    <>
      <StageModal
        isOpen={isModalOpen}
        onClose={closeModal}
        currentStage={status}
        changeStatus={changeStatus}
        isOnlyGeneralWhenSuggestion={isOnlyGeneralWhenSuggestion}
      />

      <div
        className={cn("dropdown", {
          "dropdown--is-active": isModalOpen,
          "dropdown--high": isHigh,
        })}
        style={statusButtonStyles}
        onClick={openModal}>
        {/* @ts-ignore */}
        <StatusDot $color={status?.color} />
        <span data-testid="teeup-stage">{statusText[status?.label]}</span>
        <div
          className={cn("dialog-button__dropdown-arrow", {
            "arrow-color__orange": status?.label === planning,
            "arrow-color__green": status?.label === allset,
            "arrow-color__blue": status?.label === happening,
            "arrow-color__purple": status?.label === happeningSoon,
            "arrow-color__gray": status?.label === ended,
            "arrow-color__red": status?.label === cancelled,
          })}>
          <SvgIcon name="arrow-2" color={status?.color} />
        </div>
      </div>
    </>
  )
}

const mapStateToProps = state => {
  return {
    userId: selectUserId(state),
    active: selectActiveTeeup(state),
  }
}

export default connect(mapStateToProps, null)(DropDown)
